import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import './backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalShort, fmtLocalDate } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Row types
// ─────────────────────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRow = Record<string, any>;

type ViewId = 'missing-coc' | 'due-samples' | 'sample-tracking' | 'pending-transfer' |
              'subout-tracking' |
              'subout-pending-registration' |
              'sample-prep' | 'sdms-result-entry' | 'data-review' | 'reporting' |
              'result-approval' |
              'icm01' | 'icm02' | 'icm03' | 'icm05';

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────
// Shared helper (§14b.6): local tz, 24-hour, no AM/PM, force-UTC inside.
function fmtDatetime(v?: string | null): string {
  return fmtLocalShort(v) || '—';
}
function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}
function isPast(v?: string | null) { return !!v && new Date(v) < new Date(); }

// ─────────────────────────────────────────────────────────────────────────────
// Column definitions
// ─────────────────────────────────────────────────────────────────────────────
interface ColDef {
  key: string; header: string; width?: string;
  cls?: (r: AnyRow) => string;
  render?: (v: unknown, r: AnyRow) => React.ReactNode;
}

const COLS: Record<ViewId, ColDef[]> = {
  'missing-coc': [
    { key:'jobId',        header:'Job ID',        width:'110px', cls:()=>'jobid' },
    { key:'projectId',    header:'Project ID',    width:'160px' },
    { key:'client',       header:'Client' },
    { key:'receivedDate', header:'Date Received', width:'170px', render:v=>fmtDatetime(v as string) },
  ],
  'due-samples': [
    { key:'jobId',        header:'Job ID',        width:'110px', cls:()=>'jobid' },
    { key:'sampleId',     header:'Sample ID',     width:'120px' },
    { key:'sampleName',   header:'Sample Name',   width:'160px' },
    { key:'testName',     header:'Test',          width:'180px' },
    { key:'method',       header:'Method',        width:'120px' },
    { key:'dueDate',      header:'Due Date',      width:'100px', cls:r=>isPast(r.dueDate)?'alert':'', render:v=>fmtDate(v as string) },
    { key:'receivedDate', header:'Received',      width:'100px', render:v=>fmtDate(v as string) },
    { key:'client',       header:'Client' },
    { key:'projectId',    header:'Project ID',    width:'120px' },
  ],
  'sample-tracking': [
    { key:'jobId',        header:'Job ID',        width:'110px', cls:()=>'jobid' },
    { key:'client',       header:'Client' },
    { key:'projectId',    header:'Project ID',    width:'150px' },
    { key:'receivedDate', header:'Date Received', width:'170px', render:v=>fmtDatetime(v as string) },
    { key:'dueDate',      header:'Due Date',      width:'100px', cls:r=>isPast(r.dueDate)?'warn':'', render:v=>fmtDate(v as string) },
    { key:'sampleCount',  header:'Samples',       width:'80px',  cls:()=>'num' },
    { key:'testCount',    header:'Tests',         width:'70px',  cls:()=>'num' },
  ],
  // Pending Transfer — mirrors ALPACA dashboard "Backlog 02 - Pending Transfer"
  // grid (JobId | SampleID | Client | ProjectID | ProjectName | Test).
  'pending-transfer': [
    { key:'jobId',       header:'JobID',       width:'110px', cls:()=>'jobid' },
    { key:'sampleId',    header:'SampleID',    width:'150px' },
    { key:'client',      header:'Client' },
    { key:'projectId',   header:'ProjectID',   width:'150px' },
    { key:'projectName', header:'ProjectName', width:'180px' },
    { key:'test',        header:'Test',        width:'220px' },
    { key:'status',      header:'Status',      width:'150px',
      render:v=><span className="bl-pill warn">{v as string || 'Pending Transfer'}</span> },
  ],
  // Subout Tracking — column order + captions mirror XAF GoLive's
  // "Subout Result Entry" grid exactly (user feedback 2026-05-31).
  // Dropped Shipped/Due Date; added SampleID + DeliveryService.
  'subout-tracking': [
    { key:'orderId',         header:'SuboutOrderID',    width:'130px', cls:()=>'jobid' },
    { key:'jobId',           header:'JobID',            width:'110px' },
    { key:'sampleId',        header:'SampleID',         width:'140px' },
    { key:'testDescription', header:'Test',             width:'200px' },
    { key:'contractLab',     header:'ContractLabName' },
    { key:'deliveryService', header:'DeliveryService',  width:'160px' },
    { key:'status',          header:'Status',           width:'150px',
      render:v=><span className={`bl-pill warn`}>{v as string || 'Pending'}</span> },
  ],
  // Pending Subout Registration — XAF GoLive "Pending Subout Registration" tab.
  // Samples flagged for subout but not yet registered with a contract lab.
  // 2026-05-31 polish: dropped empty Method column (no prep-method for subouts),
  // renamed MethodNumber → Method caption (this is the analytical method).
  'subout-pending-registration': [
    { key:'jobId',           header:'JobID',            width:'110px', cls:()=>'jobid' },
    { key:'sampleId',        header:'SampleID',         width:'140px' },
    { key:'testName',        header:'Test',             width:'200px' },
    { key:'methodNumber',    header:'Method',           width:'140px' },
    { key:'matrix',          header:'Matrix',           width:'110px' },
    { key:'numberOfSamples', header:'NumberOfSamples',  width:'140px', cls:()=>'num' },
  ],
  // Sample Prep — column order + captions mirror XAF "Pending Prep Sample Details"
  // (user feedback 2026-05-31): JobID | SampleID | Matrix | Test | Method |
  // PrepMethod | PrepType | ReceivedDate.  Other DTO fields kept but hidden.
  'sample-prep': [
    { key:'jobId',          header:'JobID',        width:'110px', cls:()=>'jobid' },
    { key:'sampleId',       header:'SampleID',     width:'140px' },
    { key:'matrix',         header:'Matrix',       width:'130px' },
    { key:'testName',       header:'Test',         width:'200px' },
    { key:'method',         header:'Method',       width:'120px' },
    { key:'prepMethod',     header:'PrepMethod',   width:'120px' },
    { key:'prepType',       header:'PrepType',     width:'150px',
      render:v=><span className="bl-pill info">{(v as string) || '—'}</span> },
    { key:'receivedDate',   header:'ReceivedDate', width:'150px', render:v=>fmtDatetime(v as string) },
  ],
  // SDMS Result Entry — column order + captions mirror XAF "Pending Result
  // Entry" (user feedback 2026-05-31):
  // JobID | SampleID | Test | HoldtimeExpiryDate | DateCollected |
  // ReceivedDate | ProjectID | Client | status
  // XAF screen: JobID | SampleID | Test | HoldtimeExpiryDate | DateCollected
  // | ReceivedDate | ProjectID | Client | Status.  Old extra columns
  // (Method, Analyzed, Due Date) dropped to match.
  'sdms-result-entry': [
    { key:'jobId',              header:'JobID',              width:'110px', cls:()=>'jobid' },
    { key:'sampleId',           header:'SampleID',           width:'140px' },
    { key:'testName',           header:'Test',               width:'180px' },
    // Holdtime is a preformatted countdown STRING from the SP ("12d 0hr 30min";
    // negative = past expiry) — render verbatim, not as a date (2026-07-01 fix).
    { key:'holdtimeExpiryDate', header:'HoldtimeExpiryDate', width:'170px',
      render:v=>{ const s=(v as string)||''; return <span>{s||'—'}</span>; } },
    { key:'dateCollected',      header:'DateCollected',      width:'170px', render:v=>fmtDatetime(v as string) },
    { key:'receivedDate',       header:'ReceivedDate',       width:'160px', render:v=>fmtDatetime(v as string) },
    { key:'projectId',          header:'ProjectID',          width:'120px' },
    { key:'client',             header:'Client' },
    { key:'status',             header:'status',             width:'160px',
      render:v=><span className="bl-pill warn">{(v as string) || 'Pending Result Entry'}</span> },
  ],
  // Data Review — column set + order EXACTLY like ALPACA "Result Validation"
  // (2026-08-31 user request): JobID | QCBatchID | SysSampleID | SampleName |
  // Test | Method | Parameter | ResultNumeric | Result | UnitName | AnalyzedDate
  // | AnalyzedBy | STATUS. The SP returns more columns (RptLimit/MDL/LOQ/
  // Validated*/Matrix/MethodName/Component) but ALPACA's grid shows only these.
  'data-review': [
    { key:'jobId',         header:'JobID',         width:'110px', cls:()=>'jobid' },
    { key:'qcBatchID',     header:'QCBatchID',     width:'130px' },
    { key:'sysSampleID',   header:'SysSampleID',   width:'140px' },
    { key:'sampleName',    header:'SampleName',    width:'150px' },
    { key:'testName',      header:'Test',          width:'180px' },
    { key:'method',        header:'Method',        width:'120px' },
    { key:'parameter',     header:'Parameter',     width:'160px' },
    { key:'resultNumeric', header:'ResultNumeric', width:'120px', cls:()=>'num' },
    { key:'result',        header:'Result',        width:'110px' },
    { key:'unitName',      header:'UnitName',      width:'100px' },
    { key:'analyzedDate',  header:'AnalyzedDate',  width:'150px', render:v=>fmtDatetime(v as string) },
    { key:'analyzedBy',    header:'AnalyzedBy',    width:'150px' },
    { key:'status',        header:'STATUS',        width:'150px' },
  ],
  // Reporting — column order + captions mirror XAF "Custom Reporting" grid
  // (user feedback 2026-05-31): JobID | DateCollected | ReceivedDate | ProjectID
  // | ProjectName | Client | DueDate | Status
  'reporting': [
    { key:'jobId',         header:'JobID',         width:'110px', cls:()=>'jobid' },
    { key:'dateCollected', header:'DateCollected', width:'160px', render:v=>fmtDatetime(v as string) },
    { key:'receivedDate',  header:'ReceivedDate',  width:'160px', render:v=>fmtDatetime(v as string) },
    { key:'projectID',     header:'ProjectID',     width:'180px' },
    { key:'projectName',   header:'ProjectName',   width:'180px' },
    { key:'client',        header:'Client' },
    { key:'dueDate',       header:'DueDate',       width:'120px', cls:r=>isPast(r.dueDate)?'warn':'', render:v=>fmtDate(v as string) },
    { key:'status',        header:'Status',        width:'160px',
      render:v=><span className="bl-pill warn">{(v as string) || 'PendingReporting'}</span> },
  ],
  // Result Approval — mirrors the legacy DevExpress "Result Approval" dashboard:
  // JobID | Client | ProjectID | ProjectName | Test | Method | DateCollected |
  // ReceivedDate | DueDate | Status | ClientCount.  Dates are raw DATETIME from
  // the SP, rendered in the viewer's local tz (24h) via the shared fmtLocal*
  // helpers (fmtDatetime / fmtDate) per §14b.6 — same as the Reporting view.
  'result-approval': [
    { key:'jobId',         header:'JobID',         width:'110px', cls:()=>'jobid' },
    { key:'client',        header:'Client' },
    { key:'projectId',     header:'ProjectID',     width:'150px' },
    { key:'projectName',   header:'ProjectName',   width:'180px' },
    { key:'test',          header:'Test',          width:'180px' },
    { key:'method',        header:'Method',        width:'130px' },
    { key:'dateCollected', header:'DateCollected', width:'160px', render:v=>fmtDatetime(v as string) },
    { key:'receivedDate',  header:'ReceivedDate',  width:'160px', render:v=>fmtDatetime(v as string) },
    { key:'isFieldTest',   header:'FieldTest',     width:'90px',
      // Boolean cell → the shared read-only .cellcheck glyph (BO UI Standard).
      render:v=> <span style={{ display:'block', textAlign:'center' }}>
        <span className={`cellcheck${v ? ' on' : ''}`} />
      </span> },
    { key:'status',        header:'Status',        width:'160px',
      render:v=><span className="bl-pill warn">{(v as string) || 'Pending Approval'}</span> },
  ],
  // ICM02 — XAF "Items" grid: VendorName | ItemName | Description | #CatLog
  // | Unit | Qty (Sum) | NeededBy | Department | RequestedBy | RequestDate
  'icm02': [
    { key:'vendorName',    header:'VendorName',    width:'180px' },
    { key:'itemName',      header:'ItemName',      width:'200px' },
    { key:'description',   header:'Description' },
    { key:'catLog',        header:'#CatLog',       width:'120px' },
    { key:'unit',          header:'Unit',          width:'80px' },
    { key:'qty',           header:'Qty (Sum)',     width:'90px',  cls:()=>'num' },
    { key:'neededBy',      header:'NeededBy',      width:'130px' },
    { key:'department',    header:'Department',    width:'120px' },
    { key:'requestedBy',   header:'RequestedBy',   width:'130px' },
    { key:'requestDate',   header:'RequestDate',   width:'120px', render:v=>fmtDate(v as string) },
  ],
  // ICM01 — XAF "Items" grid: VendorName | ItemName | Specification | #CatLog
  // | Unit | Qty | NeededBy | Department | Requester | Validator
  'icm01': [
    { key:'vendorName',    header:'VendorName',    width:'180px' },
    { key:'itemName',      header:'ItemName',      width:'200px' },
    { key:'specification', header:'Specification' },
    { key:'catLog',        header:'#CatLog',       width:'130px' },
    { key:'unit',          header:'Unit',          width:'80px' },
    { key:'qty',           header:'Qty',           width:'70px',  cls:()=>'num' },
    { key:'neededBy',      header:'NeededBy',      width:'130px' },
    { key:'department',    header:'Department',    width:'120px' },
    { key:'requester',     header:'Requester',     width:'130px' },
    { key:'validator',     header:'Validator',     width:'130px' },
  ],
  // ICM03 — XAF "Items" grid: RequestedDate | ItemName | VendorName | #CatLog
  // | Qty (Sum) | Unit | RequestedBy | PO# | LT# | ValidatedBy | OrderedDate
  // | OrderedBy | ReceivedDate | ReceivedBy | Department | Requisition#
  'icm03': [
    { key:'requestedDate',     header:'RequestedDate',     width:'130px', render:v=>fmtDate(v as string) },
    { key:'itemName',          header:'ItemName',          width:'200px' },
    { key:'vendorName',        header:'VendorName',        width:'160px' },
    { key:'catLog',            header:'#CatLog',           width:'120px' },
    { key:'qty',               header:'Qty (Sum)',         width:'90px',  cls:()=>'num' },
    { key:'unit',              header:'Unit',              width:'70px' },
    { key:'requestedBy',       header:'RequestedBy',       width:'120px' },
    { key:'poNumber',          header:'PO#',               width:'120px' },
    { key:'lt',                header:'LT#',               width:'90px' },
    { key:'validatedBy',       header:'ValidatedBy',       width:'120px' },
    { key:'orderedDate',       header:'OrderedDate',       width:'120px', render:v=>fmtDate(v as string) },
    { key:'orderedBy',         header:'OrderedBy',         width:'120px' },
    { key:'receivedDate',      header:'ReceivedDate',      width:'120px', render:v=>fmtDate(v as string) },
    { key:'receivedBy',        header:'ReceivedBy',        width:'120px' },
    { key:'department',        header:'Department',        width:'120px' },
    { key:'requisitionNumber', header:'Requisition#',      width:'130px' },
  ],
  // ICM05 — XAF: ItemName | Catlog# | Department | RC# | Vendor | VendorLT# | LT#
  'icm05': [
    { key:'itemName',    header:'ItemName',    width:'250px' },
    { key:'catLogNo',    header:'Catlog#',     width:'130px' },
    { key:'department',  header:'Department',  width:'120px' },
    { key:'rcNumber',    header:'RC#',         width:'130px' },
    { key:'vendorName',  header:'Vendor',      width:'180px' },
    { key:'vendorLt',    header:'VendorLT#',   width:'140px' },
    { key:'lt',          header:'LT#',         width:'120px' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// View metadata
// ─────────────────────────────────────────────────────────────────────────────
interface ViewMeta { title: string; icon: string; desc: string; unit: string; }
const VIEW_META: Record<ViewId, ViewMeta> = {
  'missing-coc':      { title:'00 - Missing COC',               icon:'ti-alert-circle',  desc:'Open jobs with no COC attached',                    unit:'job' },
  'due-samples':      { title:'Backlog 00 — Due Samples',        icon:'ti-clock',         desc:'Tests past due date, not yet analyzed',              unit:'test' },
  'sample-tracking':  { title:'Backlog 01 — Sample Tracking',    icon:'ti-map-pin',       desc:'Jobs at Pending Submission stage',                   unit:'job' },
  'pending-transfer': { title:'Backlog 02 — Pending Transfer',   icon:'ti-transfer',      desc:'Sampling-job samples awaiting bottle transfer',      unit:'sample' },
  'subout-tracking':  { title:'Backlog 03 — Subout Tracking',    icon:'ti-transfer',      desc:'Sub-contract orders pending result entry',           unit:'order' },
  'subout-pending-registration': { title:'Backlog 03 — Pending Subout Registration', icon:'ti-transfer', desc:'Samples flagged for subout, not yet registered', unit:'test' },
  'sample-prep':      { title:'Backlog 04 — Sample Preparation', icon:'ti-test-pipe',     desc:'Tests awaiting prep batch assignment',               unit:'test' },
  'sdms-result-entry':{ title:'Backlog 05 — SDMS Result Entry',  icon:'ti-table',         desc:'Transferred batches awaiting result entry',          unit:'test' },
  'data-review':      { title:'Backlog 06 — Data Review',        icon:'ti-checkup-list',  desc:'Tests pending validation',                          unit:'test' },
  'result-approval':  { title:'Backlog 07 — Result Approval',    icon:'ti-circle-check',  desc:'Jobs/tests at Pending Approval (awaiting approval)',  unit:'test' },
  'reporting':        { title:'Backlog 08 — Reporting',          icon:'ti-report',        desc:'Jobs with tests at Pending Reporting stage',         unit:'job' },
  'icm02':            { title:'ICM02 — Pending Validation',      icon:'ti-clock-pause',   desc:'Requisitions submitted, awaiting manager approval',  unit:'requisition' },
  'icm01':            { title:'ICM01 — Pending Orders',          icon:'ti-shopping-cart', desc:'Approved requisitions, PO not yet created',          unit:'requisition' },
  'icm03':            { title:'ICM03 — Pending Receipt',         icon:'ti-truck',         desc:'PO created, items not yet received',                 unit:'line' },
  'icm05':            { title:'ICM05 — Pending COA',             icon:'ti-certificate',   desc:'Received items with no Certificate of Analysis',     unit:'item' },
};

// KPI cards were removed from every Sample Backlog dashboard (user request
// 2026-08-31) — the grid is the whole view now, ALPACA-style. computeKpis /
// KpiCard and their KPI-only helpers (daysSince / startOfWeek / isThisWeek /
// distinct) were deleted with them.

// ─────────────────────────────────────────────────────────────────────────────
// Generic filterable data table — thin wrapper over the shared <AppDataGrid>.
// Keeps the page-level search box + CSV export OUTSIDE the grid (the grid owns
// per-column filter, sort, pagination, footer and load-time).
// ─────────────────────────────────────────────────────────────────────────────

// Colour/weight presets matching the legacy .bl-tbl cell classes (backlog.css).
// Structural/severity colours via theme tokens (ink / amber / red).
const CLS_STYLE: Record<string, React.CSSProperties> = {
  jobid: { fontWeight: 600, color: 'var(--ai-ink)' },
  warn:  { color: 'var(--ai-amber)', fontWeight: 600 },
  alert: { color: 'var(--ai-red)', fontWeight: 600 },
};

/** Convert one legacy ColDef → AppColumn.  `cls` colour classes become an inline
 *  style in a render wrapper; `num` becomes right-alignment; any existing
 *  `render(v,r)` is preserved and given a `text` so the column still
 *  filters / sorts on a plain string. */
function toAppColumn(c: ColDef, rows: AnyRow[]): AppColumn<AnyRow> {
  // Probe one row's class to decide alignment / colour (cls is value-dependent
  // only for date-style alert/warn columns, so per-row styling is applied in render).
  const sample = rows[0];
  const probeCls = c.cls && sample ? c.cls(sample) : '';
  const isNum = probeCls.split(' ').includes('num');

  const plainText = (r: AnyRow): string => {
    const v = r[c.key];
    return v === null || v === undefined ? '' : String(v);
  };

  const col: AppColumn<AnyRow> = {
    field: c.key,
    header: c.header,
    width: c.width,
    align: isNum ? 'right' : undefined,
    text: plainText,
  };

  // A cell needs a custom render when it formats a value, shows a pill, or
  // carries a colour/weight class.
  if (c.render || c.cls) {
    col.render = (r: AnyRow) => {
      const v = r[c.key];
      const inner = c.render ? c.render(v, r) : (v != null ? String(v) : '—');
      const cls = c.cls ? c.cls(r) : '';
      // Pick up colour/weight from the first matching style class (jobid/warn/alert).
      const styleKey = cls.split(' ').find(k => CLS_STYLE[k]);
      if (styleKey) {
        return <span style={CLS_STYLE[styleKey]}>{inner}</span>;
      }
      return inner;
    };
  }

  return col;
}

interface DataTableProps { cols: ColDef[]; rows: AnyRow[]; loading: boolean; loadMs: number | null; gridTitle?: string; }

function DataTable({ cols, rows, loading, loadMs, gridTitle }: DataTableProps) {
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState<Set<string | number>>(new Set());

  // Stable row id by object identity — AppDataGrid calls getRowId with a
  // PAGE-relative index for body rows but a FULL-list index for select-all,
  // so an index-based id would desync selection.  Keyed on the row reference
  // (the same objects persist across filter/sort), it stays consistent.
  const idMap = useRef(new WeakMap<AnyRow, number>());
  const idSeq = useRef(0);
  const rowId = useCallback((row: AnyRow): number => {
    let id = idMap.current.get(row);
    if (id === undefined) { id = idSeq.current++; idMap.current.set(row, id); }
    return id;
  }, []);

  // Clear selection when the underlying data changes (view switch / refresh).
  useEffect(() => { setSelected(new Set()); }, [rows]);

  // Page-level free-text search only — AppDataGrid does per-column filter + sort + pagination.
  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(row =>
      cols.some(c => { const v = row[c.key]; return v && String(v).toLowerCase().includes(s); }),
    );
  }, [rows, search, cols]);

  // Map legacy ColDef[] → AppColumn[] (memoised per cols + first-row probe).
  const gridCols = useMemo(() => cols.map(c => toAppColumn(c, rows)), [cols, rows]);

  return (
    <>
      {/* ── Search bar — shared .list-search-row ON the grey page (BO UI
          Standard v2), never inside the card. ── */}
      <div className="list-search-row">
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input
            type="text" placeholder="Search job ID, project, client…"
            value={search} onChange={e => setSearch(e.target.value)}
          />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
        </div>
      </div>

      {/* ONE white grid card — grid + its single footer only. */}
      <div className="bl-card bl-card-flex">
        {/* Shared data grid — styling, per-column filters, sort, footer /
            pagination, load-time and selection checkboxes standardised here. */}
        <AppDataGrid<AnyRow>
          rows={filtered}
          columns={gridCols}
          getRowId={rowId}
          loading={loading}
          loadMs={loadMs}
          maximizable
          gridTitle={gridTitle}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          emptyText="No items in this queue"
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Nav ID map (ALPACA nav IDs → our ViewId)
// ─────────────────────────────────────────────────────────────────────────────
const NAV_ID_MAP: Record<string, ViewId> = {
  'bl-coc':     'missing-coc',
  'bl-due':     'due-samples',
  'bl-track':   'sample-tracking',
  'bl-transfer':'pending-transfer',
  'bl-subout':  'subout-tracking',
  'bl-prep':    'sample-prep',
  'bl-sdms':    'sdms-result-entry',
  'bl-review':  'data-review',
  'bl-report':  'reporting',
  'bl-rappr':   'result-approval',
  'icm01':      'icm01',
  'icm02':      'icm02',
  'icm03':      'icm03',
  'icm05':      'icm05',
};

// Reverse of NAV_ID_MAP — resolves the active ViewId back to its sidebar nav id
// so <FormTitle> sources the icon/colour from the SAME nav entry as the sidebar.
// subout-pending-registration has no own nav id → falls back to its sibling.
const VIEW_TO_NAV_ID: Record<ViewId, string> = {
  ...(Object.fromEntries(
    Object.entries(NAV_ID_MAP).map(([navId, view]) => [view, navId]),
  ) as Record<ViewId, string>),
  'subout-pending-registration': 'bl-subout',
};

// ─────────────────────────────────────────────────────────────────────────────
// API endpoint map
// ─────────────────────────────────────────────────────────────────────────────
const ENDPOINT: Record<ViewId, string> = {
  'missing-coc':                 '/api/v1/backlog/missing-coc',
  'due-samples':                 '/api/v1/backlog/due-samples',
  'sample-tracking':             '/api/v1/backlog/sample-tracking',
  'pending-transfer':            '/api/v1/backlog/pending-transfer',
  'subout-tracking':             '/api/v1/backlog/subout-tracking',
  'subout-pending-registration': '/api/v1/backlog/subout-pending-registration',
  'sample-prep':                 '/api/v1/backlog/sample-prep',
  'sdms-result-entry':           '/api/v1/backlog/sdms-result-entry',
  'data-review':                 '/api/v1/backlog/data-review',
  'reporting':                   '/api/v1/backlog/reporting',
  'result-approval':             '/api/v1/backlog/result-approval',
  'icm01':                       '/api/v1/backlog/icm-pending-orders',
  'icm02':                       '/api/v1/backlog/icm-pending-validation',
  'icm03':                       '/api/v1/backlog/icm-pending-receipt',
  'icm05':                       '/api/v1/backlog/icm-pending-coa',
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
interface Props { initialView?: string; }

export default function SampleBacklogPage({ initialView }: Props) {
  const resolvedInit: ViewId = (initialView && NAV_ID_MAP[initialView])
    ? NAV_ID_MAP[initialView]
    : (initialView as ViewId | undefined) ?? 'missing-coc';

  const [activeView, setActiveView] = useState<ViewId>(resolvedInit);
  const [rows,    setRows]    = useState<AnyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [error,   setError]   = useState<string>();
  const abortRef = useRef<AbortController>();

  const loadView = useCallback(async (view: ViewId) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(undefined); setRows([]);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: AnyRow[] }>(ENDPOINT[view], { signal: ctrl.signal });
      if (!ctrl.signal.aborted) {
        if (res.data.success) setRows(res.data.data);
        else setError('Failed to load data.');
      }
    } catch (e: unknown) {
      if (!ctrl.signal.aborted) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        if (msg !== 'canceled' && msg !== 'CanceledError') setError(msg);
      }
    } finally {
      if (!ctrl.signal.aborted) { setLoadMs(performance.now() - t0); setLoading(false); }
    }
  }, []);

  // Sync view when parent navigates to a different bl-* item
  useEffect(() => {
    if (!initialView) return;
    const mapped: ViewId = NAV_ID_MAP[initialView] ?? (initialView as ViewId);
    setActiveView(mapped);
  }, [initialView]);

  useEffect(() => { loadView(activeView); }, [activeView, loadView]);

  const meta  = VIEW_META[activeView];
  const cols  = COLS[activeView];
  // Shared full-format export uses the SAME AppColumns the grid renders (via
  // toAppColumn), so the export cells match the grid display text exactly.
  const exportCols = useMemo(() => cols.map(c => toAppColumn(c, rows)), [cols, rows]);
  const unit  = meta.unit;
  const count = rows.length;
  const subtitle = `Read-only · ${loading ? '…' : count} ${unit}${count===1?'':'s'}`;

  return (
    // KPI stat cards removed from every Sample Backlog dashboard (user request
    // 2026-08-31) — the grid is the whole view, ALPACA-style. No `kpis-on`.
    <div className="bl-page">
      {/* Fixed top section — header + KPIs */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Backlog</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{meta.title}</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta + the TOP load-time chip. */}
          <div className="dp-title-row">
            <FormTitle pageId={VIEW_TO_NAV_ID[activeView]} title={meta.title} loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">{subtitle}</div>
        </div>

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{fontSize:14,verticalAlign:'middle',marginRight:5}} />
            {error}
          </div>
        )}

        {/* Subout Tracking has two sibling views — shared .sa-filter-* chips
            (backlog.css) render the tab toggle in the standard chip style. */}
        {(activeView === 'subout-tracking' || activeView === 'subout-pending-registration') && (
          <div className="sa-filter-row" style={{ borderBottom: 'none' }}>
            {([
              { id:'subout-tracking',             label:'Subout Result Entry' },
              { id:'subout-pending-registration', label:'Pending Subout Registration' },
            ] as { id: ViewId; label: string }[]).map(t => (
              <button key={t.id}
                className={`sa-filter-btn${activeView === t.id ? ' active' : ''}`}
                onClick={() => setActiveView(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        )}

      </div>

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — read-only backlog (no CRUD):
            Refresh · Export to.  The Maximize control lives on the grid's OWN
            header now (AppDataGrid `maximizable`, ALPACA dashboard-grid parity). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={() => loadView(activeView)}
            refreshing={loading}
            exportData={{ filename: meta.title, ...gridExportMatrix(rows, exportCols) }}
          />
        </div>
        {/* Grid-header caption mirrors ALPACA's short panel title ("Result
            Approval"), i.e. meta.title without the "Backlog NN —" prefix. */}
        <DataTable cols={cols} rows={rows} loading={loading} loadMs={loadMs}
          gridTitle={meta.title.includes('—') ? meta.title.split('—').pop()!.trim() : meta.title} />
      </div>
    </div>
  );
}
